import { Checkbox, DatePicker, Modal, Space, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const { Text } = Typography

type Props = {
  open: boolean
  title: string
  /** Body copy explaining that a new token is issued. */
  description: ReactNode
  okText: string
  loading?: boolean
  /**
   * Receives the new expiry (ISO string, or null when none) and whether the
   * previous credential should be revoked.
   */
  onConfirm: (expiresAt: string | null, revokeOld: boolean) => void
  onCancel: () => void
}

/**
 * Shared rotate confirmation: optionally sets a new expiry for the freshly
 * issued token and lets the operator revoke the previous credential (off by
 * default — the old token keeps working until then). The name carries over
 * automatically (handled server-side), so it isn't editable here. Used by both
 * the per-app credentials panel and the public-access credentials page.
 */
export const RotateCredentialModal = ({
  open,
  title,
  description,
  okText,
  loading,
  onConfirm,
  onCancel
}: Props) => {
  const { t } = useTranslation()
  const [expiresAt, setExpiresAt] = useState<Dayjs | null>(null)
  const [revokeOld, setRevokeOld] = useState(false)

  // Reset both fields on every (re)open (adjusting state during render) so a
  // draft never leaks into the next credential — matches CredentialActionModal.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setExpiresAt(null)
      setRevokeOld(false)
    }
  }

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      cancelText={t('cancel', 'Cancel')}
      confirmLoading={loading}
      onOk={() =>
        onConfirm(expiresAt ? expiresAt.toISOString() : null, revokeOld)
      }
      onCancel={() => {
        if (!loading) onCancel()
      }}
      destroyOnHidden
    >
      <Space vertical size={12} style={{ width: '100%' }}>
        {typeof description === 'string' ? (
          <Text>{description}</Text>
        ) : (
          description
        )}
        <div style={{ width: '100%' }}>
          <Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
          >
            {t('credentials.newExpiryLabel')} ({t('credentials.optional')})
          </Text>
          <DatePicker
            showTime
            style={{ width: '100%' }}
            value={expiresAt}
            onChange={setExpiresAt}
            placeholder={t('credentials.newExpiryPlaceholder')}
            disabledDate={(current) => current && current.isBefore(dayjs())}
          />
        </div>
        <Checkbox
          checked={revokeOld}
          onChange={(e) => setRevokeOld(e.target.checked)}
        >
          {t('credentials.revokeOldLabel')}
        </Checkbox>
      </Space>
    </Modal>
  )
}
